import type { BoxProps } from '@withjoy/joykit';
import { Box } from '@withjoy/joykit';
import { ChevronLeftSquare, ChevronRightSquare } from '@withjoy/joykit/icons';
import type { MouseEventHandler } from 'react';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';
import { Container, DotsContainer, DotsTrack, Dot, FilledNavButton, MinimalNavButton, StyledImage } from './PhotoCarousel.styles';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { addSuppressedError } from '@shared/utils/logger';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTimeout } from '@shared/hooks/useTimeout';

type NavigationVariant = 'filled' | 'minimal';
type NavigationVisibility = 'default' | 'hover';

const DOT_WIDTH = 6; // width of each dot in px
const DOT_GAP = 8; // gap between dots in px
const VISIBLE_DOTS_COUNT = 5;
const BRIEF_ARROW_MOBILE_DISPLAY_MS = 1000;

// Page-wide so carousels showing the same hotel photos report a dead src only once.
const reportedFailedSrcs = new Set<string>();

const createSliderSettings = (onBeforeChange: (current: number, next: number) => void): Settings => ({
  infinite: false,
  speed: 500,
  dots: false,
  arrows: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  beforeChange: onBeforeChange,
  lazyLoad: 'ondemand',
});

interface CarouselButtonProps
  extends Readonly<{
    onClick: MouseEventHandler;
    side: 'left' | 'right';
    variant?: NavigationVariant;
  }> {}

const navigationConfig = {
  Icons: {
    left: ChevronLeftSquare,
    right: ChevronRightSquare,
  },
  filled: {
    Component: FilledNavButton,
    IconSize: 24,
  },
  minimal: {
    Component: MinimalNavButton,
    IconSize: 16,
  },
} as const;

const NavigationButton = ({ onClick, side, variant = 'filled' }: CarouselButtonProps) => {
  const config = navigationConfig[variant];
  const Icon = navigationConfig.Icons[side];
  const ButtonComponent = config.Component;
  const iconSize = config.IconSize;

  return (
    <ButtonComponent
      onClick={onClick}
      side={side}
    >
      <Icon size={iconSize} />
    </ButtonComponent>
  );
};

export type PhotoCarouselProps<T = unknown> = BoxProps &
  Readonly<{
    extraInfo: T;
    context: string;
    images: ReadonlyArray<Readonly<{ src: string; alt: string }>>;
    aspectRatio?: string;
    navigation?: Readonly<{
      variant: NavigationVariant;
      visible: NavigationVisibility;
    }>;
    imgProps?: BoxProps<'img'>;
    children?: React.ReactNode;
  }>;

export function PhotoCarousel<T = unknown>({
  extraInfo,
  context,
  images,
  aspectRatio,
  navigation = { variant: 'filled', visible: 'hover' },
  imgProps,
  children,
  ...restProps
}: PhotoCarouselProps<T>) {
  const { tq } = useTranslation('joykit');
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderKey, setSliderKey] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(() => new Set());
  // Mirrors failedImages synchronously so repeat errors fired before the next render are ignored.
  const failedImagesRef = useRef<Set<string>>(new Set());
  const isMobileOrTablet = useIsMobileOrTablet();
  const [showArrows, setShowArrows] = useState(true);
  const timeout = useTimeout();

  useEffect(() => {
    if (isMobileOrTablet && navigation.visible !== 'default') {
      timeout.set(() => setShowArrows(false), BRIEF_ARROW_MOBILE_DISPLAY_MS);
    } else {
      timeout.clear();
      setShowArrows(true);
    }
  }, [isMobileOrTablet, navigation.visible, timeout]);

  const displayImages = useMemo(() => {
    const validImages = images.filter(img => {
      const src = img.src?.trim();
      return src && !failedImages.has(src);
    });
    return validImages.length > 0 ? validImages : [{ src: DEFAULT_HOTEL_IMAGE, alt: tq`Default hotel image` }];
  }, [images, tq, failedImages]);

  const handleImageError = (rawSrc: string) => {
    const src = rawSrc.trim();
    if (src === DEFAULT_HOTEL_IMAGE || failedImagesRef.current.has(src)) return;
    failedImagesRef.current.add(src);

    if (!reportedFailedSrcs.has(src) && !isBotClient()) {
      reportedFailedSrcs.add(src);
      addSuppressedError(`PhotoCarousel: Image failed to load`, { context, extraInfo, src });
    }

    const failed = new Set(failedImagesRef.current);
    const remainingCount = images.filter(img => {
      const imgSrc = img.src?.trim();
      return imgSrc && !failed.has(imgSrc);
    }).length;
    setFailedImages(failed);
    setSliderKey(prev => prev + 1);
    setCurrentSlide(prev => Math.min(prev, Math.max(0, remainingCount - 1)));
  };

  const imagesSrcKey = images.map(img => img.src).join('\n');
  useEffect(() => {
    failedImagesRef.current = new Set();
    setFailedImages(new Set());
    setCurrentSlide(0);
    setSliderKey(prev => prev + 1);
  }, [imagesSrcKey]);

  const handleNextClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    sliderRef.current?.slickNext();
  };

  const handlePrevClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    sliderRef.current?.slickPrev();
  };

  const handleDotClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    const indexAttr = e.currentTarget.getAttribute('data-index');
    const index = indexAttr ? parseInt(indexAttr, 10) : 0;
    sliderRef.current?.slickGoTo(index);
  };

  // Calculate the offset for the dots track
  const calculateDotsOffset = () => {
    const totalWidth = DOT_WIDTH + DOT_GAP; // width of one dot + gap

    // Calculate the center position (where the middle dot should be)
    const centerIndex = Math.floor(VISIBLE_DOTS_COUNT / 2);

    // Calculate how many positions we need to move from the center
    const offset = -(currentSlide - centerIndex) * totalWidth;

    // Clamp the offset to prevent over-scrolling at the start and end
    const maxOffset = 0;
    const minOffset = -((displayImages.length - VISIBLE_DOTS_COUNT) * totalWidth);

    return Math.min(maxOffset, Math.max(minOffset, offset));
  };

  const settings = createSliderSettings((_, next) => setCurrentSlide(next));

  // When aspectRatio is provided, the root Box self-sizes via CSS aspect-ratio.
  // When omitted, the root Box has no intrinsic height — consumers must supply
  // height via flex stretch (e.g. alignSelf="stretch"), explicit height prop, or
  // a sized parent. The inner Container (position: absolute; inset: 0) and
  // fillHeight slick styles rely on this resolved height.
  return (
    <Box
      position="relative"
      width="100%"
      aspectRatio={aspectRatio}
      overflow="hidden"
      backgroundColor="mono2"
      {...restProps}
    >
      <Container
        position="absolute"
        inset={0}
        visible={navigation.visible}
        hideSlider={displayImages.length === 1}
        fillHeight={!aspectRatio}
      >
        <Slider
          key={sliderKey}
          {...settings}
          ref={sliderRef}
        >
          {displayImages.map(image => (
            <Box
              key={image.src}
              width="100%"
              height="100%"
            >
              <StyledImage
                as="img"
                {...imgProps}
                alt={image.alt}
                aspectRatio={aspectRatio}
                src={image.src}
                onError={() => handleImageError(image.src)}
              />
            </Box>
          ))}
        </Slider>
        {displayImages.length > 1 && (
          <>
            {showArrows && currentSlide > 0 && (
              <NavigationButton
                onClick={handlePrevClick}
                side="left"
                variant={navigation.variant}
              />
            )}
            {showArrows && currentSlide < displayImages.length - 1 && (
              <NavigationButton
                onClick={handleNextClick}
                side="right"
                variant={navigation.variant}
              />
            )}
            <DotsContainer>
              <DotsTrack offset={calculateDotsOffset()}>
                {displayImages.map((_, index) => (
                  <Dot
                    key={index}
                    active={index === currentSlide}
                    data-index={index}
                    onClick={handleDotClick}
                  />
                ))}
              </DotsTrack>
            </DotsContainer>
          </>
        )}
      </Container>
      {children}
    </Box>
  );
}
